import { UIEvent, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { faBell } from '@fortawesome/pro-light-svg-icons/faBell';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import throttle from 'lodash/throttle';
import isEmpty from 'lodash/isEmpty';
import useOnclickOutside from 'react-cool-onclickoutside';
import Loader from 'epn-ui/umd/dist/components/Loader';

import { faRedoAlt } from '@fortawesome/pro-regular-svg-icons/faRedoAlt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useI18n from '@i18n';

import { HeaderNotificationsViewModel } from '../../interfaces';
import IconButton from '../IconButton';
import IconButtonDropdown from '../IconButtonDropdown';

import NotificationCard from './NotificationCard';
import styles from './index.module.scss';

interface INotificationsMenuProps {
    notifications: HeaderNotificationsViewModel;
}

const NotificationsMenu: React.FC<INotificationsMenuProps> = ({ notifications }) => {
    const { t } = useI18n('Base');
    const [active, setActive] = useState(false);
    const [isRefreshing, setRefreshing] = useState(false);

    const notificationsList = useOnclickOutside(() => setActive(false));

    const icon = active ? faTimes : faBell;
    const isUnreadMessageExist = !!notifications.items.find(({ isRead }) => !isRead);

    const handleNotificationsScroll = useCallback(
        (target: HTMLDivElement) => {
            const unreadNotificationsIds = notifications.items
                .filter(({ isRead }) => !isRead)
                .map(({ id }) => id);

            notifications.markRead(unreadNotificationsIds);

            const isScrolledToBottom =
                target.scrollHeight - target.scrollTop === target.clientHeight;

            if (isScrolledToBottom) notifications.loadNextPage();
        },
        [notifications],
    );

    const loadMoreNotifications = useMemo(() => throttle(handleNotificationsScroll, 1000), [
        handleNotificationsScroll,
    ]);

    useEffect(() => {
        return () => {
            loadMoreNotifications.cancel();
        };
    }, [loadMoreNotifications]);

    const refreshNotifications = async () => {
        setRefreshing(true);
        await notifications.refresh();

        setTimeout(() => {
            setRefreshing(false);
        }, 700);
    };

    const requestId = notifications.error?.response?.headers?.get('request-id');

    return (
        <div ref={notificationsList} className="flex h-full md:relative">
            <IconButton
                onClick={() => {
                    setActive(!active);
                }}
                active={active}
                icon={icon}
                unreadFlag={isUnreadMessageExist}
                id="NotificationButton"
                aria-label="toggle notifications"
            />

            {active && (
                <IconButtonDropdown className={styles.popup}>
                    <div className={clsx('flex justify-between p-4', styles.header)}>
                        <h5 className="font-medium text-sub text-blueGray-800">
                            {t('Notifications')}
                        </h5>
                        <button
                            type="button"
                            className={clsx('text-blueGray-500', {
                                'animate-spin': isRefreshing,
                            })}
                            id="NotificationRefreshButton"
                            onClick={refreshNotifications}
                        >
                            <FontAwesomeIcon className="text-blueGray-500" icon={faRedoAlt} />
                        </button>
                    </div>
                    <div className={styles.listWrapper}>
                        <div
                            className={styles.list}
                            onScroll={(event: UIEvent<HTMLDivElement>) =>
                                loadMoreNotifications(event.currentTarget)
                            }
                        >
                            {notifications.isLoading && <Loader />}

                            {Array.isArray(notifications.items) &&
                                notifications.items.map(d => (
                                    <NotificationCard key={d.id} href={d.link}>
                                        {d.message}
                                    </NotificationCard>
                                ))}

                            {isEmpty(notifications.items) &&
                                !notifications.isLoading &&
                                !notifications.error && (
                                    <div className="p-5 text-center text-p1">
                                        {t('No notifications')}
                                    </div>
                                )}

                            {notifications.error && (
                                <div className="p-5 text-center text-p1">
                                    {t('Failed to load data')}{' '}
                                    {requestId && (
                                        <p className="font-medium">request-id: {requestId}</p>
                                    )}
                                </div>
                            )}

                            {notifications.isLoading && <Loader />}
                        </div>

                        {!isEmpty(notifications.items) && (
                            <div className={styles.fade} aria-hidden />
                        )}
                    </div>
                </IconButtonDropdown>
            )}
        </div>
    );
};

export default NotificationsMenu;
